import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AppContext";

function AuthShell({ title, eyebrow, children, footer }) {
  return (
    <div className="wb-container grid min-h-[70vh] items-center gap-16 py-16 lg:grid-cols-12">
      <div className="lg:col-span-6 hidden lg:block">
        <div className="eyebrow">{eyebrow}</div>
        <p className="mt-6 font-display text-5xl leading-tight tracking-tight">Your collection, <em className="gold">kept safe.</em></p>
        <p className="mt-6 max-w-md text-sm muted leading-relaxed">Sign in to stream your soundscapes and films, re-download prints, and follow every order.</p>
      </div>
      <div className="lg:col-span-5 lg:col-start-8 wb-card p-8 sm:p-10 fade-up">
        <h1 className="font-display text-4xl tracking-tight">{title}</h1>
        {children}
        <div className="mt-8 text-xs muted">{footer}</div>
      </div>
    </div>
  );
}

function useAuthSubmit(path) {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const submit = async (body) => {
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await api.post(path, body);
      if (!data.token) { setNotice(data.message); setBusy(false); return; }
      signIn(data);
      toast.success(`Welcome${data.user.name ? `, ${data.user.name}` : ""}`);
      nav(params.get("next") || "/library");
    } catch (e) {
      setError(errMsg(e)); setBusy(false);
    }
  };
  return { submit, busy, error, notice };
}

export function Login() {
  const [f, setF] = useState({ email: "", password: "" });
  const { submit, busy, error } = useAuthSubmit("/auth/login");
  return (
    <AuthShell eyebrow="Account" title="Sign in" footer={<>New here? <Link to="/register" className="gold" data-testid="login-register-link">Create an account</Link></>}>
      <form className="mt-8 space-y-5" onSubmit={(e) => { e.preventDefault(); submit(f); }} data-testid="login-form">
        <div><label className="wb-label">Email</label><input type="email" required className="wb-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
        <div><label className="wb-label">Password</label><input type="password" required className="wb-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
        <div className="text-right -mt-2"><Link to="/forgot-password" className="text-xs muted hover:text-wb-gold" data-testid="login-forgot-link">Forgot password?</Link></div>
        {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
        <button className="btn-gold w-full" disabled={busy} data-testid="login-form-submit-button">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const { submit, busy, error, notice } = useAuthSubmit("/auth/register");
  if (notice)
    return (
      <AuthShell eyebrow="Join" title="Almost there" footer={<Link to="/" className="gold">Back to the gallery</Link>}>
        <p className="mt-6 text-sm leading-relaxed muted" data-testid="register-pending-notice">{notice}</p>
      </AuthShell>
    );
  return (
    <AuthShell eyebrow="Join" title="Create account" footer={<>Already collecting? <Link to="/login" className="gold" data-testid="register-login-link">Sign in</Link></>}>
      <form className="mt-8 space-y-5" onSubmit={(e) => { e.preventDefault(); submit(f); }} data-testid="register-form">
        <div><label className="wb-label">Name</label><input required className="wb-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" /></div>
        <div><label className="wb-label">Email</label><input type="email" required className="wb-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" /></div>
        <div><label className="wb-label">Password (min 6)</label><input type="password" minLength={6} required className="wb-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" /></div>
        {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
        <button className="btn-gold w-full" disabled={busy} data-testid="register-form-submit-button">{busy ? "Creating…" : "Create account"}</button>
      </form>
    </AuthShell>
  );
}
